import type { ApiResponse } from '@/api/transport';
import type {
  CanvasConnection,
  CanvasDocument,
  CanvasNode,
  CanvasShare,
  CanvasSummary,
  VideoNode,
} from '@/api/types';
import { mockCanvasNodes, mockConnections, mockVideos } from '@/mocks/data';
import { videoNodeFromVideo } from '@/features/canvas/documentFromVideos';
import { route } from './handlers';
import { getState, patchState } from './state';

const ok = (body: unknown, status = 200): ApiResponse => ({ status, body });
const fail = (status: number, message: string, error?: string): ApiResponse => ({
  status,
  body: { message, error },
});

type StoredDoc = { nodes: CanvasNode[]; connections: CanvasConnection[]; version: number };

function docs(): Record<string, StoredDoc> {
  return ((getState() as unknown as { canvasDocs?: Record<string, StoredDoc> }).canvasDocs ??
    {}) as Record<string, StoredDoc>;
}

function saveDocs(next: Record<string, StoredDoc>): void {
  patchState({ canvasDocs: next } as never);
}

const nowIso = () => new Date().toISOString();

function videoNode(
  canvasId: number,
  videoId: number,
  index: number,
  baseY: number,
): VideoNode | null {
  const video = mockVideos.find((v) => v.id === videoId);
  return video ? videoNodeFromVideo(canvasId, video, index, baseY) : null;
}

/** Документ: демо-сцена прототипа для канваса 1, для остальных — видео канваса сеткой. */
function buildDocument(canvas: CanvasSummary): StoredDoc {
  const stored = docs()[String(canvas.id)];
  const videoIds = getState().canvasVideos[String(canvas.id)] ?? [];
  const base: StoredDoc = stored ?? {
    nodes: canvas.id === 1 ? mockCanvasNodes.map((n) => ({ ...n })) : [],
    connections: canvas.id === 1 ? mockConnections.map((c) => ({ ...c })) : [],
    version: 1,
  };
  // Видео, добавленные из каталога после последнего сохранения, докладываются ниже существующих узлов.
  const present = new Set(
    base.nodes.filter((n): n is VideoNode => n.type === 'video').map((n) => n.video_reference_id),
  );
  const missing = videoIds.filter((id) => !present.has(id));
  if (missing.length === 0) return base;
  const bottom = base.nodes.length ? Math.max(...base.nodes.map((n) => n.y + n.height)) + 80 : 120;
  const added = missing
    .map((id, i) => videoNode(canvas.id, id, i, bottom))
    .filter((n): n is VideoNode => !!n);
  return { ...base, nodes: [...base.nodes, ...added] };
}

function toDocument(canvas: CanvasSummary): CanvasDocument {
  const doc = buildDocument(canvas);
  return {
    canvas: { ...canvas, nodes_count: doc.nodes.length },
    nodes: doc.nodes,
    connections: doc.connections,
    version: doc.version,
  };
}

route('GET', '/canvases/:id/document', ({ params }) => {
  const canvas = getState().canvases.find((c) => c.id === Number(params['id']));
  if (!canvas) return fail(404, 'Not found');
  return ok({ data: toDocument(canvas) });
});

route('PUT', '/canvases/:id/document', ({ params, body }) => {
  const id = Number(params['id']);
  const s = getState();
  const canvas = s.canvases.find((c) => c.id === id);
  if (!canvas) return fail(404, 'Not found');
  const nodes = Array.isArray(body['nodes']) ? (body['nodes'] as CanvasNode[]) : [];
  const connections = Array.isArray(body['connections'])
    ? (body['connections'] as CanvasConnection[])
    : [];
  const previous = docs()[String(id)];
  const version = (previous?.version ?? 1) + 1;
  saveDocs({ ...docs(), [id]: { nodes, connections, version } });
  // Состав видео канваса следует за узлами — карточки каталога и Saved узнают об этом (CNV-21).
  const videoIds = [
    ...new Set(
      nodes
        .filter((n): n is VideoNode => n.type === 'video')
        .map((n) => n.video_reference_id)
        .filter((v): v is number => v !== null),
    ),
  ];
  const updated_at = nowIso();
  const canvases = getState().canvases.map((c) =>
    c.id === id ? { ...c, nodes_count: nodes.length, updated_at } : c,
  );
  patchState({ canvases, canvasVideos: { ...getState().canvasVideos, [id]: videoIds } });
  return ok({ version, updated_at, nodes_count: nodes.length });
});

// ——— Файлы канваса (canvas-spec.md §5): в mock картинка живёт как object URL этой вкладки ———
const ULID_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const mockUlid = () =>
  Array.from({ length: 26 }, () => ULID_ALPHABET[Math.floor(Math.random() * 32)]).join('');

route('POST', '/canvases/:id/assets', ({ params, body }) => {
  const canvas = getState().canvases.find((c) => c.id === Number(params['id']));
  if (!canvas) return fail(404, 'Not found');
  const file = body instanceof FormData ? body.get('file') : null;
  if (!(file instanceof Blob)) return fail(422, 'File is not a supported image.', 'not_an_image');
  const id = mockUlid();
  const url =
    typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : `blob:mock/${id}`;
  return ok(
    {
      data: {
        id,
        kind: 'image',
        url,
        mime: file.type || 'image/png',
        size_bytes: file.size,
        width: null,
        height: null,
      },
    },
    201,
  );
});

// ——— Участники (canvas-spec.md §7) ———
let nextParticipantId = 500;
const initialsOfEmail = (email: string) =>
  email
    .split('@')[0]!
    .split(/[._-]+/)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

route('GET', '/canvases/:id/participants', ({ params }) => {
  const canvas = getState().canvases.find((c) => c.id === Number(params['id']));
  return canvas ? ok({ data: canvas.participants }) : fail(404, 'Not found');
});

route('POST', '/canvases/:id/participants', ({ params, body }) => {
  const s = getState();
  const canvas = s.canvases.find((c) => c.id === Number(params['id']));
  if (!canvas) return fail(404, 'Not found');
  if (!s.fullAccess)
    return fail(403, 'Inviting people is available on the paid plan', 'paid_feature');
  const email = String(body['email'] ?? '')
    .trim()
    .toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return fail(422, 'Enter a valid email');
  if (canvas.participants.some((p) => p.email === email))
    return fail(409, 'Invitation already sent', 'already_invited');
  const participant = {
    id: nextParticipantId++,
    user_id: null,
    name: email,
    initials: initialsOfEmail(email),
    role: (body['role'] === 'viewer' ? 'viewer' : 'editor') as 'editor' | 'viewer',
    via: 'invite' as const,
    status: 'pending' as const,
    email,
  };
  patchState({
    canvases: s.canvases.map((c) =>
      c.id === canvas.id ? { ...c, participants: [...c.participants, participant] } : c,
    ),
  });
  return ok({ data: participant }, 201);
});

route('DELETE', '/canvases/:id/participants/:participantId', ({ params }) => {
  const s = getState();
  const id = Number(params['id']);
  const participantId = Number(params['participantId']);
  patchState({
    canvases: s.canvases.map((c) =>
      c.id === id
        ? {
            ...c,
            participants: c.participants.filter((p) => (p.id ?? p.user_id) !== participantId),
          }
        : c,
    ),
  });
  return ok({ ok: true });
});

route('POST', '/invites/:token/accept', () => {
  const first = getState().canvases[0];
  return first
    ? ok({ data: { canvas_id: first.id, role: 'editor' } })
    : fail(404, 'Invitation not found or already used', 'invite_not_found');
});

route('POST', '/shared/canvases/:token/join', ({ params }) => {
  const canvas = getState().canvases.find((c) => c.share?.token === params['token']);
  if (!canvas || !canvas.share) return fail(404, 'Share link not found', 'share_not_found');
  if (canvas.share.mode !== 'edit') return fail(403, 'This link is view only', 'link_is_view_only');
  return ok({ data: { canvas_id: canvas.id, role: 'editor' } });
});

// ——— Шаринг (CNV-23) ———
route('POST', '/canvases/:id/share', ({ params, body }) => {
  const id = Number(params['id']);
  const s = getState();
  const canvas = s.canvases.find((c) => c.id === id);
  if (!canvas) return fail(404, 'Not found');
  // Бесплатный тариф: делиться канвасом — платная возможность.
  if (!s.fullAccess)
    return fail(403, 'Sharing canvases is available on the paid plan', 'paid_feature');
  const mode = body['mode'] === 'edit' ? 'edit' : 'view';
  const raw = body['duration_days'];
  const days = raw === 7 || raw === 30 || raw === 90 ? raw : null;
  const share: CanvasShare = {
    token: canvas.share?.token ?? `share-${id}-${Math.random().toString(36).slice(2, 10)}`,
    mode,
    shared_at: nowIso(),
    expires_at: days ? new Date(Date.now() + days * 86_400_000).toISOString() : null,
    status: 'active',
    views_count: canvas.share?.views_count ?? 0,
  };
  patchState({ canvases: s.canvases.map((c) => (c.id === id ? { ...c, share } : c)) });
  return ok({ data: share });
});

route('DELETE', '/canvases/:id/share', ({ params }) => {
  const id = Number(params['id']);
  const s = getState();
  patchState({
    canvases: s.canvases.map((c) =>
      c.id === id && c.share ? { ...c, share: { ...c.share, status: 'revoked' } } : c,
    ),
  });
  return ok({ ok: true });
});

// ——— Публичный канвас (CNV-24): 410 для истёкшей / отозванной ссылки ———
route('GET', '/shared/canvases/:token', ({ params }) => {
  const token = params['token'] ?? '';
  if (token === 'expired-token') return fail(410, 'This share link has expired', 'share_expired');
  const canvas = getState().canvases.find((c) => c.share?.token === token);
  if (!canvas || !canvas.share) return fail(404, 'Share link not found', 'share_not_found');
  if (canvas.share.status !== 'active')
    return fail(410, 'This share link was revoked', 'share_revoked');
  if (canvas.share.expires_at && new Date(canvas.share.expires_at).getTime() < Date.now())
    return fail(410, 'This share link has expired', 'share_expired');
  const s = getState();
  patchState({
    canvases: s.canvases.map((c) =>
      c.id === canvas.id && c.share
        ? { ...c, share: { ...c.share, views_count: c.share.views_count + 1 } }
        : c,
    ),
  });
  return ok({ data: toDocument(canvas), mode: canvas.share.mode });
});

// ——— Видео по ссылке площадки (CNV-2): mock подбирает видео каталога по площадке ———
route('POST', '/canvases/resolve-link', ({ body }) => {
  const url = typeof body['url'] === 'string' ? body['url'].trim() : '';
  if (!/^https?:\/\//.test(url))
    return fail(422, 'Paste a link to YouTube, Instagram, TikTok or Facebook', 'invalid_url');
  const platform = /instagram\.com/.test(url)
    ? 'instagram'
    : /tiktok\.com/.test(url)
      ? 'tiktok'
      : /facebook\.com|fb\.watch/.test(url)
        ? 'facebook'
        : /youtube\.com|youtu\.be/.test(url)
          ? 'youtube'
          : null;
  if (!platform) return fail(422, 'This platform is not supported yet', 'unsupported_platform');
  const pick = mockVideos[(url.length * 7) % 20]!;
  return ok({
    video_reference_id: pick.id,
    title: pick.title,
    source_url: url,
    poster_url: pick.extra?.thumbnail_url ?? null,
    duration_sec: pick.duration_sec,
    embed_only: pick.media === null,
  });
});
